import {
    FilterExpression,
    FilterExpressionHash,
    IColumnRequest,
    IComposeFilterField,
    IFilterField,
    IFilterType,
    IQueryDSLLookupParams,
    IServerRollupRequest,
    ISortByRequest,
    FilterType,
    ColumnType,
    RollupTotalKind,
} from "helpers/query-dsl-types";
import { ColumnVO } from "ag-grid-community/dist/lib/interfaces/iColumnVO";
import {
    ALLOCATION_DIMENSION_ID_PREFIX,
    FILTER_KEY,
    Kinds,
    SMU_FILTER,
    ValueTypes,
} from "constants/product-collection";
import { List, Map } from "immutable";
import {
    AllocationDimensionIndex,
    AllocationDimensionKind,
    getAllocationDimensionByIndex,
    IAllocationDimension,
    isAllocationDimensionId,
} from "./allocation-dimension";
import { IClasificationHierarchy, parseDimensionColumn } from "./door-collection";
import { Metric } from "./metrics/types";
import { translateCellBooleanInput } from "./objUtils";
import { parseAssortmentName, parseStringDateIntoServerFormat } from "./product-collection";
import { getFilterFieldType } from "./query-helpers";

import {
    AggFuncTypes,
    AggregationFuncGridToServerMap,
    ASSORTMENT_FIELD_NAME,
    ASSORTMENT_NAME_DELIMITER,
    buildDoorGroupNameSelected,
    COLLECTION_ID_FIELD_NAME,
    COL_DEF_SEPARATOR,
    DOOR_DIMENSION_METRICS_GROUP_NAME,
    DOOR_METRICS_GROUP_NAME_SEPARATOR,
    FieldValueType,
    getBlankTextValue,
    HASHTAGS_LIST_ITEMS_SIZE_PAGE,
    HASHTAG_AUTO_COMPLETE_FIELD,
    IAttributeDoorsFilter,
    IDoorGroupMetric,
    IDoorGroupMetricName,
    isFieldWithCustomSorting,
    mapCollectionFieldValue,
    NU_COLLECTION_NAME_FIELD,
    NU_CUSTOM_SORT_PREFIX,
    ROLLUP_SIZE_PAGE,
    UNSET_GROUP_KEY,
} from "./server-rollup";
import { nullish } from "./nullish";
import { Attribute } from "reducers/dimensions-panel/dimensions-panel";
import { SortModelItem } from "ag-grid-community";

export const EMPTY_SET_FILTER_VALUE = "<UNKOWN>";
const INDEX_NOT_FOUND = -1;

const buildItemValueByType = (valueType: ValueTypes, value: any) => {
    switch (valueType) {
        case ValueTypes.BOOL:
            return { bool_value: translateCellBooleanInput(value) };
        case ValueTypes.NUMBER:
            return { number_value: value };
        case ValueTypes.DATE:
        default:
            return { string_value: `${value}` };
    }
};

const sanitizeValueType = (valueType: ValueTypes) => (valueType === ValueTypes.DATE ? ValueTypes.STRING : valueType);

const sanitizeValue = (
    itemDisplayProperty: List<Map<string, any>>,
    value: any,
    fieldName: string,
    merchantDateFormat: string
) => {
    const schemaField = itemDisplayProperty.find((field) => field && field.get("field_name") === fieldName);
    let theValue =
        fieldName === ASSORTMENT_FIELD_NAME
            ? parseAssortmentName(value, schemaField?.get("field_value_delimiter") || ASSORTMENT_NAME_DELIMITER)
                  .assortmendId || value
            : value;
    const valueType =
        theValue === UNSET_GROUP_KEY ? ValueTypes.STRING : schemaField?.get("value_type") || ValueTypes.STRING;
    if (valueType === ValueTypes.DATE) {
        theValue = parseStringDateIntoServerFormat(theValue, merchantDateFormat);
    } else if (valueType === ValueTypes.NUMBER && theValue === "") {
        theValue = 0;
    }

    return { value: theValue, valueType, schemaField };
};

const buildOrderClause = (
    itemDisplayProperty: List<Map<string, any>>,
    fields?: SortModelItem[],
    deliveryIds?: string[],
    valueCols?: ColumnVO[],
    currentGroup?: ColumnVO,
    useAggSorting?: boolean,
    hasAllocationDimensionsGroup?: boolean,
    groupKeys?: string[]
) => {
    if (!fields?.length) {
        return {};
    }

    const sortGroupIndex =
        useAggSorting && currentGroup ? fields.findIndex((field) => field.colId === currentGroup.id) : -1;
    const sanitizedFields = sortGroupIndex === -1 ? fields : [fields[sortGroupIndex]];
    return {
        sort_by: sanitizedFields.reduce<ISortByRequest[]>((acc, { colId, sort }) => {
            const schemaField = itemDisplayProperty.find((field) => field && field.get("field_name") === colId);
            const fieldType = getFilterFieldType(schemaField);
            const isCustomSort = isFieldWithCustomSorting(schemaField);
            const theColId = isCustomSort ? `${NU_CUSTOM_SORT_PREFIX}${colId}` : colId;

            const dimensionNodes = parseDimensionColumn(theColId, deliveryIds);
            if (dimensionNodes) {
                const { doorId, deliveryId, isAllDimensionTotal, isDimensionTotal } = dimensionNodes;

                return acc.concat({
                    total_units: {
                        door_id: isAllDimensionTotal ? "" : isDimensionTotal ? "" : doorId || "",
                        delivery_id: isAllDimensionTotal ? "" : deliveryId || "",
                    },
                    order: sort.toUpperCase() as any,
                });
            }

            if (useAggSorting) {
                const aggFieldSort = valueCols?.find((vc) => vc.id === colId);
                const isSecondaryColumn = theColId.includes(COL_DEF_SEPARATOR);
                const isSortingByGroup = currentGroup?.id === colId;
                if ((isSecondaryColumn && valueCols) || aggFieldSort || isSortingByGroup) {
                    if (isSecondaryColumn || (aggFieldSort && !isSortingByGroup)) {
                        const aggFieldName = theColId.split(COL_DEF_SEPARATOR).reverse()[0];
                        const valueColumn = aggFieldSort
                            ? aggFieldSort
                            : valueCols.filter((vc) => vc.id === aggFieldName)[0];
                        const [aggColumn] = mapAggregationFilters(
                            itemDisplayProperty,
                            [valueColumn],
                            null,
                            hasAllocationDimensionsGroup,
                            true
                        );
                        return acc.concat({
                            aggregation: {
                                column_id: theColId,
                                column: aggColumn,
                            },
                            order: sort.toUpperCase() as any,
                        });
                    }

                    return acc.concat({
                        aggregation: {
                            column_id: "",
                        },
                        order: sort.toUpperCase() as any,
                    });
                }
            }

            if (theColId === ASSORTMENT_FIELD_NAME) {
                return acc.concat([
                    {
                        field: {
                            field: NU_COLLECTION_NAME_FIELD,
                            value_type: ValueTypes.STRING,
                            field_type: fieldType,
                        },
                        order: sort.toUpperCase() as any,
                    },
                    {
                        field: {
                            field: COLLECTION_ID_FIELD_NAME,
                            value_type: ValueTypes.STRING,
                            field_type: fieldType,
                        },
                        order: sort.toUpperCase() as any,
                    },
                ]);
            }

            return acc.concat({
                field: {
                    field: theColId,
                    value_type: sanitizeValueType(
                        isCustomSort ? ValueTypes.NUMBER : schemaField?.get("value_type") || ValueTypes.STRING
                    ),
                    field_type: fieldType,
                },
                order: sort.toUpperCase() as any,
            });
        }, []),
    };
};

const getInRangeFilter = (schemaField: Map<string, any>, field: IFilterField, key: string) => {
    return {
        and: {
            filters: [
                {
                    [FilterExpressionHash.greaterThanOrEqual]: {
                        field: key,
                        value: buildItemValueByType(schemaField?.get("value_type"), field.filter),
                        type: getFilterFieldType(schemaField),
                    },
                },
                {
                    [FilterExpressionHash.lessThanOrEqual]: {
                        field: key,
                        value: buildItemValueByType(schemaField?.get("value_type"), field.filterTo),
                        type: getFilterFieldType(schemaField),
                    },
                },
            ],
        },
    };
};

const buildGlobalSearchClause = (itemDisplayProperty: List<Map<string, any>>, searchTokens: string[]) => {
    if (!searchTokens?.length) {
        return {};
    }

    return {
        search: {
            query: searchTokens.join(" "),
            fields: itemDisplayProperty.reduce((acc, schemaKey) => {
                const fieldName = schemaKey && schemaKey.get("field_name");
                const fieldValueType = schemaKey && schemaKey.get("value_type");

                fieldName && fieldValueType === ValueTypes.STRING && acc.push(fieldName);
                return acc;
            }, []),
        },
    };
};

export const mapFormulaBulkFields = (itemDisplayProperty) => {
    const schemaFormulaFields = itemDisplayProperty
        .filter((field) => field && field.get("kind") === Kinds.FORMULA && field.get("rowwise_evaluation"))
        .toJS();
    return schemaFormulaFields.map((schemaFormulaField) => ({ bulk_field: schemaFormulaField.field_name }));
};

const mapAggregationFilters = (
    itemDisplayProperty: List<Map<string, any>>,
    aggregationFilters: ColumnVO[],
    useCustomSort?: boolean,
    hasAllocationDimensionsGroup?: boolean,
    isSorting?: boolean
): IColumnRequest[] =>
    aggregationFilters.map((agg) => {
        const schemaField = itemDisplayProperty.find((field) => field && field.get("field_name") === agg.field);
        const isCustomSort = useCustomSort && isFieldWithCustomSorting(schemaField);
        let theColId = isCustomSort ? `${NU_CUSTOM_SORT_PREFIX}${agg.field}` : agg.field;

        const isAllocationDimension = isAllocationDimensionId(theColId);

        if (isAllocationDimension) {
            theColId = theColId.replace(ALLOCATION_DIMENSION_ID_PREFIX, "");
        }

        const isBulkFormula =
            schemaField && schemaField.get("kind") === Kinds.FORMULA && schemaField.get("rowwise_evaluation");
        let aggrFunction = AggregationFuncGridToServerMap[agg.aggFunc?.toUpperCase()];
        const shouldIncludeBulkFormulaField =
            [AggFuncTypes.SUM, AggFuncTypes.SUM_WITH_PERCENTAGE, AggFuncTypes.AVG].includes(aggrFunction) &&
            isBulkFormula;

        if (hasAllocationDimensionsGroup) {
            if (aggrFunction === AggFuncTypes.SUM_WITH_PERCENTAGE && !isBulkFormula) {
                aggrFunction = AggFuncTypes.SUM;
            } else if (aggrFunction === AggFuncTypes.COUNT_WITH_PERCENTAGE) {
                aggrFunction = AggFuncTypes.COUNT;
            }
        }

        const colId =
            theColId === ASSORTMENT_FIELD_NAME
                ? isSorting
                    ? NU_COLLECTION_NAME_FIELD
                    : COLLECTION_ID_FIELD_NAME
                : theColId;
        return {
            id: colId,
            display_name: agg.displayName,
            field: colId,
            aggr_func: aggrFunction,
            value_type: isCustomSort
                ? ValueTypes.NUMBER
                : sanitizeValueType(schemaField?.get("value_type") || ValueTypes.STRING),
            ...(isAllocationDimension && { type: ColumnType.AllocationDimension }),
            ...(shouldIncludeBulkFormulaField && { type: ColumnType.BulkFormulaField }),
        };
    });

export const mapGroupKeys = (
    itemDisplayProperty: List<Map<string, any>>,
    groupKeys: string[],
    groupBy: ColumnVO[],
    merchantDateFormat: string,
    allocationDimensions = []
): FieldValueType[] =>
    groupKeys.map((key, index) => {
        if (isAllocationDimensionId(groupBy[index].field)) {
            const genericAllocationDimensions = allocationDimensions.filter(
                (allocationDimension) => allocationDimension.subtype === AllocationDimensionKind.Generic
            );
            const currentAllocationDimensionIndex = genericAllocationDimensions.findIndex(
                (allocationDimension) => allocationDimension.id === groupBy[index].field
            );
            const currentAllocationDimension = genericAllocationDimensions[currentAllocationDimensionIndex];
            const currentAllocationDimensionValue = currentAllocationDimension?.dimension_values?.find(
                (dimensionValue) => dimensionValue.name === key
            );

            if (!currentAllocationDimensionValue && key === "") {
                return mapCollectionFieldValue(ValueTypes.STRING, UNSET_GROUP_KEY, true);
            }

            return mapCollectionFieldValue(ValueTypes.STRING, currentAllocationDimensionValue?.id, true);
        }
        const { value, valueType, schemaField } = sanitizeValue(
            itemDisplayProperty,
            key,
            groupBy[index].field,
            merchantDateFormat
        );
        if (isFieldWithCustomSorting(schemaField)) {
            const indexValue = schemaField
                .getIn(["constraint", "allowed_values"])
                .findIndex((val) => val.get("string_value") === key);
            return mapCollectionFieldValue(ValueTypes.NUMBER, indexValue, true);
        }
        return mapCollectionFieldValue(valueType, value, true);
    });

const buildFilterClause = (
    fields: IFilterType,
    itemDisplayProperty: List<Map<string, any>>,
    rowGroupCols: ColumnVO[],
    groupKeys: string[],
    filterAssortmentBySMU: string,
    merchantDateFormat: string
) => {
    // AgGrid filter
    const gridfilters = Object.keys(fields || {}).reduce((acc, key) => {
        const schemaField = itemDisplayProperty.find((field) => field && field.get("field_name") === key);
        const valueType = schemaField?.get("value_type") || ValueTypes.STRING;
        const fieldType = getFilterFieldType(schemaField);
        if (fields[key].hasOwnProperty("operator")) {
            const field = fields[key] as IComposeFilterField;
            const condition1Property = FilterExpressionHash[field.condition1.type];
            const condition2Property = FilterExpressionHash[field.condition2.type];
            const left =
                field.condition1.type === FilterExpression.InRange
                    ? getInRangeFilter(schemaField, field.condition1, key)
                    : {
                          [condition1Property]: {
                              field: key,
                              value: buildItemValueByType(valueType, field.condition1.filter),
                              type: fieldType,
                          },
                      };

            const right =
                field.condition2.type === FilterExpression.InRange
                    ? getInRangeFilter(schemaField, field.condition2, key)
                    : {
                          [condition2Property]: {
                              field: key,
                              value: buildItemValueByType(valueType, field.condition2.filter),
                              type: fieldType,
                          },
                      };

            acc.push({
                [field.operator.toLowerCase()]: {
                    filters: [left, right],
                },
            });
            return acc;
        }

        const field = fields[key] as IFilterField;
        if (field.filterType === FilterType.SET) {
            const setValues = [...field.values];
            const blankIndex = setValues.findIndex((v) => v === getBlankTextValue() || v === getBlankTextValue(true));
            blankIndex !== INDEX_NOT_FOUND && setValues.splice(blankIndex, 1);

            let setFilters = null;
            const blankFilter = blankIndex !== INDEX_NOT_FOUND && {
                [FilterExpressionHash.equals]: {
                    field: key,
                    value: buildItemValueByType(
                        ValueTypes.STRING,
                        valueType === ValueTypes.STRING ? "" : UNSET_GROUP_KEY
                    ),
                    type: fieldType,
                },
            };

            if (blankIndex === INDEX_NOT_FOUND && !setValues.length && valueType === ValueTypes.BOOL) {
                acc.push({
                    and: {
                        filters: [
                            {
                                [FilterExpressionHash.equals]: {
                                    field: key,
                                    value: buildItemValueByType(ValueTypes.BOOL, true),
                                    type: fieldType,
                                },
                            },
                            {
                                [FilterExpressionHash.equals]: {
                                    field: key,
                                    value: buildItemValueByType(ValueTypes.BOOL, false),
                                    type: fieldType,
                                },
                            },
                        ],
                    },
                });
            } else if (blankIndex === INDEX_NOT_FOUND || setValues.length) {
                setFilters = {
                    [FilterExpressionHash.inRange]: {
                        field: key === ASSORTMENT_FIELD_NAME ? COLLECTION_ID_FIELD_NAME : key,
                        values: setValues.length
                            ? setValues.map((value) => {
                                  const assortmendId = parseAssortmentName(
                                      value,
                                      schemaField?.get("field_value_delimiter") || ASSORTMENT_NAME_DELIMITER
                                  ).assortmendId;

                                  let theValue = key === ASSORTMENT_FIELD_NAME ? assortmendId : value;

                                  if (valueType === ValueTypes.DATE) {
                                      theValue = parseStringDateIntoServerFormat(theValue, merchantDateFormat);
                                  }
                                  return buildItemValueByType(valueType, theValue);
                              })
                            : [buildItemValueByType(valueType, EMPTY_SET_FILTER_VALUE)],
                        type: fieldType,
                    },
                };
            }

            if (setFilters && blankFilter) {
                acc.push({
                    or: {
                        filters: [blankFilter, setFilters],
                    },
                });
            } else {
                blankFilter && acc.push(blankFilter);
                setFilters && acc.push(setFilters);
            }
        } else {
            const fielterProperty = FilterExpressionHash[field.type];
            if (field.type === FilterExpression.InRange) {
                acc.push(getInRangeFilter(schemaField, field, key));
            } else {
                acc.push({
                    [fielterProperty]: {
                        field: key,
                        value: buildItemValueByType(valueType, field.filter),
                        type: fieldType,
                    },
                });
            }
        }
        return acc;
    }, []);

    const groupFilters = (groupKeys || [])
        .map((groupFilter, index) => {
            const field = rowGroupCols[index].field;
            const schemaField = itemDisplayProperty.find(
                (schemaField) => schemaField && schemaField.get("field_name") === field
            );
            const { value, valueType } = sanitizeValue(itemDisplayProperty, groupFilter, field, merchantDateFormat);

            return {
                [FilterExpressionHash.equals]: {
                    field: field === ASSORTMENT_FIELD_NAME ? COLLECTION_ID_FIELD_NAME : field,
                    value: buildItemValueByType(valueType, value),
                    type: getFilterFieldType(schemaField),
                },
            };
        })
        .filter((groupFilter) => !isAllocationDimensionId(groupFilter[FilterExpressionHash.equals].field));

    // SMU filters
    const smuFilters = [];
    if (filterAssortmentBySMU && filterAssortmentBySMU !== SMU_FILTER.All) {
        const fielterProperty =
            filterAssortmentBySMU === SMU_FILTER.SMUOnly ? FilterExpressionHash.equals : FilterExpressionHash.notEqual;
        smuFilters.push({
            [fielterProperty]: {
                field: FILTER_KEY.ProductID,
                value: buildItemValueByType(ValueTypes.STRING, ""),
                type: ColumnType.ItemField,
            },
        });
    }

    if (!gridfilters.length && !groupFilters.length && !smuFilters.length) {
        return {};
    }

    return {
        filter: {
            and: {
                filters: [...groupFilters, ...gridfilters, ...smuFilters],
            },
        },
    };
};

const getDoorsForValues = (doorsDimension: IAllocationDimension, doors: string[], values: IClasificationHierarchy[]) =>
    doorsDimension?.dimension_values
        ?.filter((it) => {
            if (doors.includes(it.id) && it.active && it.included) {
                return values.every((x) => {
                    if (!it.fields[x.field] && !x.value) {
                        return true; // SR: collection with different doors selected and custom attributes
                    }

                    return it.fields[x.field]?.string_value === x.value;
                });
            }

            return false;
        })
        ?.map((it) => it.id) || [];

const getDoorGroups = (
    attributes: Attribute[],
    doorsDimension: IAllocationDimension,
    doors: string[],
    doorGroupMetrics: (IDoorGroupMetricName & { values: IClasificationHierarchy[] })[] = [] // Acumulative value
): (IDoorGroupMetricName & { values: IClasificationHierarchy[] })[] => {
    const [parentGroup, ...restGroups] = attributes || [];
    const currentDoorGroupMetrics = (parentGroup?.values || []).reduce<
        (IDoorGroupMetricName & { values: IClasificationHierarchy[] })[]
    >((acc, value) => {
        if (value.selected) {
            const doorIds = getDoorsForValues(doorsDimension, doors, [
                {
                    field: parentGroup.id,
                    value: value.id,
                },
            ]);

            doorIds.length &&
                acc.push({
                    name: buildDoorGroupNameSelected(parentGroup.id, value.id),
                    doors: doorIds,
                    values: [{ field: parentGroup.id, value: value.id }],
                });
        }
        return acc;
    }, []);

    const doorGroupMetricsStore = doorGroupMetrics.length ? [] : currentDoorGroupMetrics;
    doorGroupMetrics.forEach((p) => {
        currentDoorGroupMetrics.reduce((acc, i) => {
            const doorIds = getDoorsForValues(doorsDimension, doors, p.values.concat(i.values));

            doorIds.length &&
                acc.push({
                    name: `${p.name}${DOOR_METRICS_GROUP_NAME_SEPARATOR}${i.name}`,
                    values: p.values.concat(i.values),
                    doors: doorIds,
                });
            return acc;
        }, doorGroupMetricsStore);
    });

    return doorGroupMetrics.concat(
        restGroups?.length
            ? getDoorGroups(restGroups, doorsDimension, doors, doorGroupMetricsStore)
            : doorGroupMetricsStore
    );
};

const buildDoorGroups = (
    attributes: Attribute[],
    doorsDimension: IAllocationDimension,
    doors: string[],
    doorGroupsDimensions: string[][]
): IDoorGroupMetricName[] => {
    const metricsCombinations = getDoorGroups(attributes, doorsDimension, doors);
    const doorsGroups = (doorGroupsDimensions?.[1] || []).map<IDoorGroupMetricName>((doorId) => ({
        name: buildDoorGroupNameSelected(DOOR_DIMENSION_METRICS_GROUP_NAME, doorId),
        doors: [doorId],
    }));

    return metricsCombinations.map(({ values, ...rest }) => rest).concat(doorsGroups);
};

const buildDoorGroupMetrics = (doorMetrics: Metric[], doorGroupsDimensions: string[][]) => {
    return (doorGroupsDimensions?.[0] || [])
        .reduce<IDoorGroupMetric[]>((acc, deliveryId) => {
            return acc.concat(
                doorMetrics.map<IDoorGroupMetric>((metric) => ({
                    metric_kind: metric.kind,
                    delivery_id: deliveryId,
                }))
            );
        }, [])
        .concat(
            doorMetrics.reduce<IDoorGroupMetric[]>((acc, metric) => {
                metric.showTotals && acc.push({ metric_kind: metric.kind });
                return acc;
            }, [])
        );
};

const buildDoorMetricsParams = (
    doorMetrics: Metric[],
    attributes: Attribute[],
    doorsDimension: IAllocationDimension,
    doorIds: string[],
    doorGroupsDimensions: string[][]
): Partial<Pick<IServerRollupRequest, "door_group_metrics" | "door_groups">> => {
    if (!doorMetrics?.length) {
        return {};
    }

    const doorGroupsParms = buildDoorGroups(attributes, doorsDimension, doorIds, doorGroupsDimensions);
    const doorGroupMetricsParamas = buildDoorGroupMetrics(doorMetrics, doorGroupsDimensions);

    return {
        ...(doorGroupMetricsParamas.length && { door_group_metrics: doorGroupMetricsParamas }),
        ...(doorGroupsParms.length && { door_groups: doorGroupsParms }),
    };
};

const buildDimensionsAndAttributesParams = (
    dateFilters: string[],
    doorFilters: string[],
    attributesDoorsFilter: IAttributeDoorsFilter
): Partial<Pick<IServerRollupRequest, "delivery_filter" | "door_filter" | "attribute_filter">> => {
    const attribute_filter = Object.keys(attributesDoorsFilter || {}).reduce<IDoorGroupMetricName[]>((acc, key) => {
        acc.push({
            name: key,
            doors: attributesDoorsFilter[key],
        });
        return acc;
    }, []);

    return {
        ...(dateFilters?.length && { delivery_filter: dateFilters }),
        ...(doorFilters?.length && { door_filter: doorFilters }),
        ...(attribute_filter.length && {
            attribute_filter,
        }),
    };
};

const buildNumberOfDoorParam = (doorIds: string[]): Partial<Pick<IServerRollupRequest, "number_of_doors">> => {
    if (!doorIds) {
        return {};
    }

    return {
        number_of_doors: doorIds.length || 0,
    };
};

// Queries

export const buildFilterProductByIdQuery = (
    merchantId: string,
    itemId: string,
    collectionId: string
): IServerRollupRequest => ({
    merchant_id: merchantId,
    page_size: 1,
    filter: {
        and: {
            filters: [
                {
                    eq: {
                        field: "id",
                        value: mapCollectionFieldValue(ValueTypes.STRING, itemId),
                        type: ColumnType.ItemField,
                    },
                },
                {
                    eq: {
                        field: COLLECTION_ID_FIELD_NAME,
                        value: mapCollectionFieldValue(ValueTypes.STRING, collectionId),
                        type: ColumnType.ItemField,
                    },
                },
            ],
        },
    },
});

export const buildFilterValuesQuery = (
    fileName: string,
    merchantId: string,
    valueType: ValueTypes,
    filterModel,
    itemDisplayProperty
): IServerRollupRequest => ({
    merchant_id: merchantId,
    page_size: ROLLUP_SIZE_PAGE,
    group_by: [{ id: fileName, display_name: fileName, field: fileName, value_type: sanitizeValueType(valueType) }],
    ...buildFilterClause(filterModel, itemDisplayProperty, [], [], "", ""),
});

export const buildFetchHashtagsQuery = (merchantId: string, searchText: string): IServerRollupRequest => ({
    merchant_id: merchantId,
    page_size: HASHTAGS_LIST_ITEMS_SIZE_PAGE,
    group_by: [{ id: HASHTAG_AUTO_COMPLETE_FIELD, field: HASHTAG_AUTO_COMPLETE_FIELD, value_type: ValueTypes.STRING }],
    group_keys: [{ string_value: `#${searchText}` }],
});

export const buildSearchProductsQuery = ({
    merchantId,
    size,
    sortModel,
    filterModel,
    itemDisplayProperty,
    searchTokens,
    searchAfter,
    merchantDateFormat,
}: Partial<IQueryDSLLookupParams>): IServerRollupRequest => {
    return {
        merchant_id: merchantId,
        page_size: size,
        ...(searchAfter?.length && { search_after: searchAfter }),
        ...buildGlobalSearchClause(itemDisplayProperty, searchTokens),
        ...(buildFilterClause(filterModel, itemDisplayProperty, [], [], "", merchantDateFormat) as any),
        ...buildOrderClause(itemDisplayProperty, sortModel),
    };
};

export const buildExportSnapshotQuery = ({
    filterModel,
    itemDisplayProperty,
    searchTokens,
    merchantDateFormat,
    filterAssortmentBySMU,
    dimensionFilters,
    attributeDoorsFilters,
}: Partial<IQueryDSLLookupParams>): IServerRollupRequest => {
    let [dateFilters, doorFilters] = dimensionFilters || [];
    dateFilters = dateFilters?.filter((it) => !nullish(it));
    doorFilters = doorFilters?.filter((it) => !nullish(it));

    return {
        ...buildDimensionsAndAttributesParams(dateFilters, doorFilters, attributeDoorsFilters),
        ...buildGlobalSearchClause(itemDisplayProperty, searchTokens),
        ...(buildFilterClause(
            filterModel,
            itemDisplayProperty,
            [],
            [],
            filterAssortmentBySMU,
            merchantDateFormat
        ) as any),
    };
};

export const buildSnapshotReviewQuery = ({
    merchantId,
    doorDimensionId,
    dateDimensionId,
    sizeDimensionId,
    productId,
    collectionId,
    doorId,
}: Partial<IQueryDSLLookupParams>): IServerRollupRequest => ({
    merchant_id: merchantId,
    door_dimension_id: doorDimensionId,
    delivery_dimension_id: dateDimensionId,
    size_dimension_id: sizeDimensionId,
    review: {
        ...(productId && { by_product: { product_id: productId, collection_id: collectionId } }),
        ...(doorId && { by_door: { door: doorId } }),
        column_header: true,
        size_columns: true,
    },
});

// Verify if groupColId is a dimensionId. If so, fill values for doorFilters, dateFilters
export const getDimensionValueFilters = (rowGroupCols, index, allocationDimensions, groupKeys) => {
    let doorFilters = [];
    let dateFilters = [];
    const currentGroupColId = rowGroupCols[index]?.id;

    if (!isAllocationDimensionId(currentGroupColId)) {
        return;
    }

    // Find the current allocationDimension to have its allocationDimensionValues
    const genericAllocationDimensions = allocationDimensions.filter(
        (allocationDimension) => allocationDimension.subtype === AllocationDimensionKind.Generic
    );
    const currentAllocationDimensionIndex = genericAllocationDimensions.findIndex(
        (allocationDimension) => allocationDimension.id === currentGroupColId
    );
    const currentAllocationDimension = genericAllocationDimensions[currentAllocationDimensionIndex];
    const isDoorDimension = currentAllocationDimensionIndex === genericAllocationDimensions.length - 1;

    if (currentAllocationDimension) {
        const currentAllocationDimensionValueId = currentAllocationDimension.dimension_values?.find(
            (dimensionValue) => dimensionValue.name === groupKeys[index]
        )?.id;

        if (currentAllocationDimensionValueId) {
            if (isDoorDimension) {
                doorFilters = [...doorFilters, currentAllocationDimensionValueId];
            } else {
                dateFilters = [...dateFilters, currentAllocationDimensionValueId];
            }
        }
    }

    return {
        doorFilters,
        dateFilters,
    };
};

// Remove empty allocationDimension when empty allocationDimension group is open
export const removeOneDimensionWhenCurrentDimensionRowGroupEmptyIsOpen = (
    groupKeys: string[] = [],
    groupBy: ColumnVO[] = [],
    items = []
) => {
    if (groupKeys.length > 0) {
        const allocationDimensionGroups = groupBy.filter((groupItem) => isAllocationDimensionId(groupItem.id));
        const hasEmptyGroupKey = groupKeys.some((groupKey) => groupKey === "");

        if (items.length > 1 && hasEmptyGroupKey) {
            if (allocationDimensionGroups.length === 1) {
                items = removeItemWhenTheisOneEmptyDimensionGroup(groupBy, allocationDimensionGroups, items);
            } else if (allocationDimensionGroups.length === 2) {
                items = removeItemWhenThereAreTwoEmptyDimensionGroups(
                    groupBy,
                    allocationDimensionGroups,
                    items,
                    groupKeys
                );
            }
        }
    }

    return items;
};

const removeItemWhenTheisOneEmptyDimensionGroup = (groupBy, allocationDimensionGroups, items) => {
    const updatedItems = [...items];
    const currentAllocationDimensionGroupIndex = groupBy.findIndex(
        (groupItem) => groupItem.id === allocationDimensionGroups[0].id
    );

    if (currentAllocationDimensionGroupIndex === groupBy.length - 1) {
        updatedItems.splice(currentAllocationDimensionGroupIndex, 1);
    }

    return updatedItems;
};

const removeItemWhenThereAreTwoEmptyDimensionGroups = (groupBy, allocationDimensionGroups, items, groupKeys) => {
    const currentRowGroupKeyIndex = groupKeys?.length - 1;
    const currentRowGroupId = groupBy[currentRowGroupKeyIndex]?.id;
    let updatedItems = [...items];
    const firstAllocationDimensionGroupIndex = groupBy.findIndex(
        (groupItem) => groupItem.id === allocationDimensionGroups[1].id
    );
    updatedItems.splice(firstAllocationDimensionGroupIndex, 1);

    if (
        isAllocationDimensionId(currentRowGroupId) &&
        currentRowGroupId === groupBy[groupBy.length - 1].id &&
        groupKeys.length > 2
    ) {
        updatedItems = updatedItems.reverse();
    }

    return updatedItems;
};

// doorGroupDimensionIds: undefined (doors deselected), [] (empty, doors selected but without selections)
export const getDoorGroupDimensions = (
    allDimensionIds: string[],
    filteredDimensionIds: string[],
    doorGroupDimensionIds?: string[]
) => {
    if (!doorGroupDimensionIds) {
        return filteredDimensionIds?.length ? filteredDimensionIds : allDimensionIds;
    }

    if (filteredDimensionIds?.length) {
        return filteredDimensionIds.filter((id) => doorGroupDimensionIds.includes(id));
    }

    return doorGroupDimensionIds;
};

export const shouldSortApply = (
    sortModel: SortModelItem[],
    groupKeys: string[],
    rowGroupCols: ColumnVO[],
    valueCols: ColumnVO[]
) => {
    let validSort = false;
    // sort only applies in grouped state if you're sorting on a column with visible data
    if (sortModel?.length && !groupKeys?.length) {
        sortModel.forEach(({ colId }) => {
            if (rowGroupCols?.length || valueCols?.length) {
                rowGroupCols?.forEach(({ id }) => {
                    if (colId === id) {
                        validSort = true;
                    }
                });
                valueCols?.forEach(({ id }) => {
                    if (colId === id) {
                        validSort = true;
                    }
                });
            } else {
                validSort = true;
            }
        });
    } else {
        validSort = true;
    }
    return validSort;
};

export const buildServerRollupQuery = ({
    merchantId,
    size,
    sortModel,
    filterModel,
    itemDisplayProperty,
    searchTokens,
    rowGroupCols,
    valueCols,
    pivotCols,
    groupKeys,
    dimensionFilters,
    filterAssortmentBySMU,
    calculateTotals,
    calculateDoorMetrics,
    totalKind = RollupTotalKind.DoorTotal,
    doorDimensionId,
    dateDimensionId,
    searchAfter,
    offset,
    merchantDateFormat,
    deliveryIds,
    doorIds,
    doorIdsWithinClassification,
    attributeDoorsFilters,
    currentGroup,
    useAggSorting,
    rollupAllocationDimensions,
    doorMetrics,
    attributes,
    doorGroupsDimensions,
    assortmentTotals,
}: IQueryDSLLookupParams): IServerRollupRequest => {
    let [dateFilters, doorFilters] = dimensionFilters || [];
    let doorIdsFiltered = doorFilters?.length ? doorFilters : doorIds || [];
    doorIdsFiltered = doorIdsWithinClassification
        ? doorIdsWithinClassification.filter((id) => doorIdsFiltered.includes(id))
        : doorIdsFiltered;
    const doorsDimension =
        rollupAllocationDimensions &&
        getAllocationDimensionByIndex(rollupAllocationDimensions, AllocationDimensionIndex.Door);

    // If groupKeys exists, it means that user is opening a group
    if (groupKeys?.length) {
        // The current group key is the last key of groupKeys.
        const lastGroupKeyIndex = groupKeys?.length - 1;
        // Verify if current group key is a dimension. If so, fill values for doorFilters, dateFilters
        const filters = getDimensionValueFilters(
            rowGroupCols,
            lastGroupKeyIndex,
            rollupAllocationDimensions,
            groupKeys
        );

        if (filters) {
            const isOpeningDoorDimension = filters.doorFilters.length > 0;
            const isOpeningDateDimension = filters.dateFilters.length > 0;
            dateFilters = isOpeningDateDimension ? filters.dateFilters : dateFilters;
            doorFilters = isOpeningDoorDimension ? filters.doorFilters : doorFilters;
        }

        if (groupKeys.length > 1) {
            // Get the size of group keys that are before the current group key
            const previousGroupKeysSize = groupKeys.length - 2;
            // Verify if previous group keys are dimensions. If so, fill values for doorFilters, dateFilters
            for (let index = previousGroupKeysSize; index >= 0; index--) {
                const filters = getDimensionValueFilters(rowGroupCols, index, rollupAllocationDimensions, groupKeys);

                if (filters) {
                    dateFilters = [...dateFilters, ...filters.dateFilters];
                    doorFilters = [...doorFilters, ...filters.doorFilters];
                }
            }
        }
    }

    const hasAllocationDimensionsGroup = !!rowGroupCols?.find((groupItem) => isAllocationDimensionId(groupItem.id));
    const validSort = shouldSortApply(sortModel, groupKeys, rowGroupCols, valueCols);

    return {
        merchant_id: merchantId,
        page_size: size,
        delivery_dimension_id: dateDimensionId?.replace(ALLOCATION_DIMENSION_ID_PREFIX, ""),
        door_dimension_id: doorDimensionId?.replace(ALLOCATION_DIMENSION_ID_PREFIX, ""),
        total: !!calculateTotals && RollupTotalKind.FooterTotal === totalKind,
        total_row: assortmentTotals || (calculateTotals && RollupTotalKind.DoorTotalRow === totalKind),
        ...(calculateTotals &&
            totalKind === RollupTotalKind.DoorTotal && {
                door_total: !!(doorDimensionId || dateDimensionId),
            }),
        ...(offset && { offset }),
        ...(searchAfter?.length && { search_after: searchAfter }),
        ...buildGlobalSearchClause(itemDisplayProperty, searchTokens),
        ...buildDimensionsAndAttributesParams(dateFilters, doorFilters, attributeDoorsFilters),
        ...(doorFilters?.length &&
            searchAfter?.length && { door_dimension_id: doorDimensionId?.replace(ALLOCATION_DIMENSION_ID_PREFIX, "") }),
        ...(buildFilterClause(
            filterModel,
            itemDisplayProperty,
            rowGroupCols,
            groupKeys,
            filterAssortmentBySMU,
            merchantDateFormat
        ) as any),
        ...(rowGroupCols?.length && {
            group_by: removeOneDimensionWhenCurrentDimensionRowGroupEmptyIsOpen(
                groupKeys,
                rowGroupCols,
                mapAggregationFilters(itemDisplayProperty, rowGroupCols, true)
            ),
        }),
        ...(pivotCols?.length && {
            pivot_by: mapAggregationFilters(itemDisplayProperty, pivotCols),
        }),
        ...(valueCols?.length && {
            value_columns: mapAggregationFilters(itemDisplayProperty, valueCols, null, hasAllocationDimensionsGroup),
        }),
        ...(groupKeys?.length && {
            group_keys: removeOneDimensionWhenCurrentDimensionRowGroupEmptyIsOpen(
                groupKeys,
                rowGroupCols,
                mapGroupKeys(
                    itemDisplayProperty,
                    groupKeys,
                    rowGroupCols,
                    merchantDateFormat,
                    rollupAllocationDimensions
                )
            ),
        }),
        ...(validSort &&
            buildOrderClause(
                itemDisplayProperty,
                sortModel,
                deliveryIds,
                valueCols,
                currentGroup,
                useAggSorting ? !!rowGroupCols.length : false,
                hasAllocationDimensionsGroup,
                groupKeys
            )),
        ...((doorFilters?.length || dateFilters?.length) && {
            formula_fields: mapFormulaBulkFields(itemDisplayProperty),
        }),
        ...([RollupTotalKind.DoorMetricsTotal, RollupTotalKind.DoorTotalRow].includes(totalKind) &&
            calculateDoorMetrics &&
            buildDoorMetricsParams(doorMetrics, attributes, doorsDimension, doorIdsFiltered, doorGroupsDimensions)),
        ...buildNumberOfDoorParam(doorIdsFiltered),
    };
};
